import React from 'react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { Anchor, Box, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { messages } from '../utils/messages.ts';
import type { UpdateGroup } from '../utils/recently-updated-types.ts';

import { ContentTypeIcon } from './ContentTypeIcon.tsx';

const styles = cssMap({
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
	},
	itemContent: {
		flex: '1',
	},
	itemMeta: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		marginBlockStart: token('space.050'),
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: token('space.050'),
	},
	titleLink: {
		textDecoration: 'none',
	},
	groupItem: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.150'),
	},
	groupContent: {
		flex: '1',
	},
	nestedList: {
		listStyle: 'none',
		padding: token('space.0'),
		margin: token('space.0'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	body: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		marginBlockStart: token('space.050'),
		fontStyle: 'italic',
	},
});

type SocialGroupProps = {
	group: UpdateGroup;
};

export function SocialGroup({ group }: SocialGroupProps) {
	const { formatMessage } = useIntl();
	const hasAuthorProfile = Boolean(group.author.profileUrl);
	const hasAuthorName = Boolean(group.author.displayName);

	const avatar = (
		<Avatar src={group.author.avatarUrl} name={group.author.displayName} size="medium" />
	);

	return (
		<Box as="li" xcss={styles.groupItem}>
			{hasAuthorProfile ? (
				<Anchor href={group.author.profileUrl} aria-label={group.author.displayName}>
					{avatar}
				</Anchor>
			) : (
				avatar
			)}
			<Box xcss={styles.groupContent}>
				{hasAuthorName &&
					(hasAuthorProfile ? (
						<Anchor href={group.author.profileUrl}>
							<Text as="strong">{group.author.displayName}</Text>
						</Anchor>
					) : (
						<Text as="strong">{group.author.displayName}</Text>
					))}
				<Box as="ul" xcss={styles.nestedList}>
					{group.items.map((item) => (
						<Box as="li" key={item.id} xcss={styles.titleRow}>
							<ContentTypeIcon contentType={item.contentType} />
							<Box xcss={styles.itemContent}>
								<Anchor href={item.url} title={item.spaceName} xcss={styles.titleLink}>
									{item.title}
								</Anchor>
								<Box xcss={styles.itemMeta}>
									<Box as="span">
										{item.version === 1
											? formatMessage(messages.createdOn, { date: item.formattedDate })
											: formatMessage(messages.updatedOn, { date: item.formattedDate })}
									</Box>
									{item.changesUrl && (
										<>
											<Box as="span" aria-hidden>
												•
											</Box>
											<Anchor href={item.changesUrl}>{formatMessage(messages.viewChanges)}</Anchor>
										</>
									)}
									{item.permLink && (
										<>
											<Box as="span" aria-hidden>
												•
											</Box>
											<Anchor href={item.permLink}>{formatMessage(messages.permalink)}</Anchor>
										</>
									)}
								</Box>
								{item.body && <Box xcss={styles.body}>{item.body}</Box>}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
}
